import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Wand2, Sliders, Download, Layers, BarChart3, Volume2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Landing() {
    const navigate = useNavigate();

    const features = [
        {
            icon: <Wand2 className="w-6 h-6" />,
            title: "AI-Powered Mastering",
            description: "Intelligent analysis and automatic recommendations for professional results",
            color: "text-[#7B2CBF]"
        },
        {
            icon: <Sliders className="w-6 h-6" />,
            title: "Full Control Suite",
            description: "EQ, compression, limiting, stereo imaging, and advanced processing tools",
            color: "text-[#D0F224]"
        },
        {
            icon: <Layers className="w-6 h-6" />,
            title: "Multi-track Mixing",
            description: "Mix multiple stems and tracks with precision level control",
            color: "text-[#00F0FF]"
        },
        {
            icon: <BarChart3 className="w-6 h-6" />,
            title: "Real-time Analysis",
            description: "Waveforms, spectrum analyzer, and LUFS metering",
            color: "text-[#FF9F1C]"
        }
    ];

    const processingModules = [
        "Parametric EQ", "Multiband Compressor", "Limiter", 
        "Stereo Imaging", "Harmonic Exciter", "De-esser", "Loudness Normalization"
    ];

    return (
        <div className="min-h-screen bg-[#050505]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D0F224] flex items-center justify-center">
                            <Volume2 className="w-6 h-6 text-black" />
                        </div>
                        <span className="font-heading text-xl font-bold tracking-tight">AudioMaster Pro</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button 
                            variant="ghost" 
                            className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                            onClick={() => navigate('/login')}
                            data-testid="nav-login-btn"
                        >
                            Log In
                        </Button>
                        <Button 
                            className="bg-[#D0F224] text-black hover:bg-[#E2F568] font-bold uppercase tracking-wider"
                            onClick={() => navigate('/register')}
                            data-testid="nav-register-btn"
                        >
                            Get Started
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center hero-bg pt-20">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img 
                        src="https://images.unsplash.com/photo-1768885512408-7bd6eb726025?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvJTIwZGFyayUyMG5lb258ZW58MHx8fHwxNzcwMzMzODk4fDA&ixlib=rb-4.1.0&q=85"
                        alt="Music studio"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-6 py-24">
                    <div className="max-w-3xl">
                        <div className="animate-fade-in">
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#7B2CBF]/20 border border-[#7B2CBF]/30 rounded-full text-sm font-medium text-[#D0F224] mb-6">
                                <Sparkles className="w-4 h-4" />
                                AI-Powered Audio Mastering
                            </span>
                            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-6">
                                Master Your Music
                                <br />
                                <span className="text-[#D0F224]">Like a Pro</span>
                            </h1>
                        </div>
                        <p className="text-lg md:text-xl text-[#A1A1AA] max-w-xl mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                            Professional-grade mixing and mastering tools powered by AI. Upload your tracks, 
                            get intelligent recommendations, and export studio-quality audio.
                        </p>
                        <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                            <Button 
                                size="lg"
                                className="bg-[#D0F224] text-black hover:bg-[#E2F568] font-bold uppercase tracking-wider px-8 py-6 text-lg group"
                                onClick={() => navigate('/register')}
                                data-testid="hero-get-started-btn"
                            >
                                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                                Start Mastering Free
                            </Button>
                            <Button 
                                size="lg"
                                variant="outline"
                                className="border-[#333] text-white hover:border-[#D0F224] hover:bg-transparent px-8 py-6 text-lg"
                                onClick={() => navigate('/login')}
                                data-testid="hero-login-btn"
                            >
                                I have an account
                            </Button>
                        </div>

                        {/* Processing modules tags */}
                        <div className="mt-12 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                            <p className="text-sm text-[#A1A1AA] mb-3">Includes:</p>
                            <div className="flex flex-wrap gap-2">
                                {processingModules.map((module, i) => (
                                    <span 
                                        key={i}
                                        className="px-3 py-1 bg-[#1E1E1E] border border-[#27272A] rounded-full text-xs text-[#A1A1AA]"
                                    >
                                        {module}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 px-6 bg-[#050505]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            Professional Mastering
                            <span className="text-[#D0F224]"> Made Simple</span>
                        </h2>
                        <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
                            Everything you need to achieve radio-ready sound
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#D0F224]/50 transition-colors duration-300 group"
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <div className={`w-12 h-12 rounded-lg bg-[#1E1E1E] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 ${feature.color}`}>
                                    {feature.icon}
                                </div>
                                <h3 className="font-heading text-lg font-bold mb-2">{feature.title}</h3>
                                <p className="text-[#A1A1AA] text-sm">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 px-6 bg-[#0A0A0A]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            Three Steps to
                            <span className="text-[#00F0FF]"> Perfect Sound</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { step: "01", title: "Upload", desc: "Drop your audio files - single tracks or multiple stems" },
                            { step: "02", title: "Analyze & Adjust", desc: "AI analyzes your audio and suggests optimal settings" },
                            { step: "03", title: "Export", desc: "Download your mastered track in high-quality format" }
                        ].map((item, i) => (
                            <div key={i} className="text-center">
                                <div className="font-heading text-6xl font-black text-[#1E1E1E] mb-4">{item.step}</div>
                                <h3 className="font-heading text-xl font-bold mb-2 text-[#D0F224]">{item.title}</h3>
                                <p className="text-[#A1A1AA]">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#7B2CBF]/10 to-[#D0F224]/10" />
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
                        Ready to Master Your Sound?
                    </h2>
                    <p className="text-[#A1A1AA] text-lg mb-8 max-w-xl mx-auto">
                        Join thousands of producers and artists achieving professional quality
                    </p>
                    <Button 
                        size="lg"
                        className="bg-[#D0F224] text-black hover:bg-[#E2F568] font-bold uppercase tracking-wider px-12 py-6 text-lg"
                        onClick={() => navigate('/register')}
                        data-testid="cta-get-started-btn"
                    >
                        Get Started Free
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-[#27272A] py-8 px-6">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#D0F224] flex items-center justify-center">
                            <Volume2 className="w-5 h-5 text-black" />
                        </div>
                        <span className="font-heading font-bold">AudioMaster Pro</span>
                    </div>
                    <p className="text-[#A1A1AA] text-sm">
                        © 2024 AudioMaster Pro. Professional audio mastering.
                    </p>
                </div>
            </footer>
        </div>
    );
}
